/**
 * Coda offline IndexedDB per upload media (FASE 14 — audit mobile).
 *
 * Quando il dispositivo è offline, le foto e i documenti vengono salvati qui.
 * Quando la connessione torna, la coda viene drenata automaticamente.
 */
import { uploadFoto } from './uploadFoto'

const DB_NAME = 'edr-mobile'
const STORE_NAME = 'pending_media'
const DB_VERSION = 1

interface PendingItem {
  id: string
  interventoId: string
  blobBase64: string
  tag: string | null
  tipo: string
  tipo_documento: string | null
  filename: string
  createdAt: number
}

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB not available'))
      return
    }
    const open = indexedDB.open(DB_NAME, DB_VERSION)
    open.onupgradeneeded = () => {
      const db = open.result
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'id' })
      }
    }
    open.onsuccess = () => resolve(open.result)
    open.onerror = () => reject(open.error)
    open.onblocked = () => reject(new Error('Blocked'))
  })
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => resolve(reader.result as string)
    reader.onerror = reject
    reader.readAsDataURL(blob)
  })
}

function base64ToBlob(base64: string): Blob {
  const parts = base64.split(',')
  const mime = parts[0].match(/:(.*?);/)?.[1] || 'application/octet-stream'
  const binary = atob(parts[1] || parts[0])
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
  return new Blob([bytes], { type: mime })
}

/** Accoda un blob alla coda offline e tenta upload immediato se online. */
export async function accodaUpload(args: {
  interventoId: string
  blob: Blob
  filename: string
  tag?: string | null
  tipo?: string
  tipo_documento?: string | null
  descrizione?: string
}): Promise<void> {
  const online = typeof navigator !== 'undefined' ? navigator.onLine : true

  if (online) {
    await uploadFoto({
      interventoId: args.interventoId,
      blob: args.blob,
      filename: args.filename,
      tag: args.tag ?? null,
      tipo: args.tipo ?? 'foto',
      tipo_documento: args.tipo_documento ?? null,
      descrizione: args.descrizione,
    })
    return
  }

  // Salva in IndexedDB per upload differito
  try {
    const db = await openDB()
    const tx = db.transaction(STORE_NAME, 'readwrite')
    const store = tx.objectStore(STORE_NAME)
    const base64 = await blobToBase64(args.blob)
    const item: PendingItem = {
      id: `${args.interventoId}-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      interventoId: args.interventoId,
      blobBase64: base64,
      tag: args.tag ?? null,
      tipo: args.tipo ?? 'foto',
      tipo_documento: args.tipo_documento ?? null,
      filename: args.filename,
      createdAt: Date.now(),
    }
    store.put(item)
    await new Promise<void>((resolve, reject) => {
      tx.oncomplete = () => resolve()
      tx.onerror = () => reject(tx.error)
    })
    db.close()
  } catch (e) {
    console.error('accodaUpload: IndexedDB write failed', e)
    throw new Error('Nessuna connessione: impossibile salvare la foto')
  }
}

/** Svuota la coda offline inviando tutti gli upload pendenti. */
export async function drainPendingQueue(): Promise<number> {
  if (typeof indexedDB === 'undefined') return 0
  const online = typeof navigator !== 'undefined' ? navigator.onLine : true
  if (!online) return 0

  try {
    const db = await openDB()
    const tx = db.transaction(STORE_NAME, 'readonly')
    const store = tx.objectStore(STORE_NAME)
    const items = await new Promise<PendingItem[]>((resolve, reject) => {
      const req = store.getAll()
      req.onsuccess = () => resolve(req.result || [])
      req.onerror = () => reject(req.error)
    })
    db.close()

    let sent = 0
    for (const item of items) {
      try {
        const blob = base64ToBlob(item.blobBase64)
        await uploadFoto({
          interventoId: item.interventoId,
          blob,
          filename: item.filename,
          tag: item.tag,
          tipo_documento: item.tipo_documento,
        })
        // Rimuovi dalla coda dopo upload riuscito
        const db2 = await openDB()
        const tx2 = db2.transaction(STORE_NAME, 'readwrite')
        tx2.objectStore(STORE_NAME).delete(item.id)
        await new Promise<void>((resolve) => { tx2.oncomplete = () => resolve() })
        db2.close()
        sent++
      } catch {
        // Lascia in coda, riprova al prossimo drenaggio
      }
    }
    return sent
  } catch {
    return 0
  }
}
